// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from './supabase';

const AuthContext = createContext({});
export const useAuth = () => useContext(AuthContext);

const APP_NAME = 'aislesync';
const BRAND_KEY = 'aislesync';
const DEFAULT_ROLE = 'associate';

const MT_TEAM = [
  'j.lutz@miskulintechnologies.com',
  'rmiskulin@gmail.com',
  'r.miskulin@miskulintechnologies.com',
  'jordan.l.lutz@gmail.com',
  'tyler.hodgson32@gmail.com',
  't.hodgson@miskulintechnologies.com',
  'threewayindustries@gmail.com',
  't.schenher@miskulintechnologies.com',
];

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onboardingStep, setOnboardingStep] = useState(null);
  const [showPasskeyPrompt, setShowPasskeyPrompt] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) fetchProfile(session.user);
      else setLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      if (session?.user) fetchProfile(session.user);
      else { setUser(null); setLoading(false); }
    });
    return () => subscription.unsubscribe();
  }, []);

  async function fetchProfile(authUser) {
    const { data } = await supabase.from('profiles').select('*').eq('id', authUser.id).single();
    const emailLower = (authUser.email || '').toLowerCase();
    const isMT = MT_TEAM.includes(emailLower);
    let role = DEFAULT_ROLE;
    if (isMT) { role = 'owner'; }
    else if (data?.role) { role = data.role; }
    else {
      const isGenesisOwner = localStorage.getItem(`${BRAND_KEY}_genesis_owner`) === emailLower;
      if (isGenesisOwner) role = 'owner';
    }
    const profile = data ? { ...authUser, ...data, role, isOwner: role === 'owner' } : { ...authUser, role, isOwner: role === 'owner' };
    setUser(profile);
    if (isMT) {
      localStorage.setItem(`${BRAND_KEY}_password_changed`, 'true');
      localStorage.setItem(`${BRAND_KEY}_nda_signed`, 'true');
      localStorage.setItem(`${BRAND_KEY}_tutorial_done`, 'true');
      setOnboardingStep(null);
    } else {
      const pwChanged = localStorage.getItem(`${BRAND_KEY}_password_changed`);
      const ndaSigned = authUser?.user_metadata?.nda_signed_at || profile.nda_signed_at || localStorage.getItem(`${BRAND_KEY}_nda_signed`);
      const tutorialDone = localStorage.getItem(`${BRAND_KEY}_tutorial_done`);
      if (!pwChanged) setOnboardingStep('password');
      else if (!ndaSigned) setOnboardingStep('nda');
      else if (!tutorialDone) setOnboardingStep('tutorial');
      else setOnboardingStep(null);
    }
    const hasPasskey = localStorage.getItem('mt_passkeys');
    const dismissedPrompt = localStorage.getItem(`${BRAND_KEY}_passkey_dismissed`);
    if (!hasPasskey && !dismissedPrompt && isMT) setShowPasskeyPrompt(true);
    setLoading(false);
  }

  async function signIn(email, password) {
    const { error } = await supabase.auth.signInWithPassword({ email: email.trim().toLowerCase(), password });
    if (error) throw error;
    try { await supabase.from('profiles').update({ app_name: APP_NAME, last_login: new Date().toISOString() }).eq('id', (await supabase.auth.getUser()).data.user.id); } catch (e) {}
  }

  async function signUp(email, password, fullName) {
    const { data: authData, error } = await supabase.auth.signUp({ email: email.trim().toLowerCase(), password, options: { data: { full_name: fullName, password_changed: true } } });
    if (error) throw error;
    if (authData.user) {
      const emailLower = email.trim().toLowerCase();
      const isMT = MT_TEAM.includes(emailLower);
      const existingGenesis = localStorage.getItem(`${BRAND_KEY}_genesis_owner`);
      let assignedRole = DEFAULT_ROLE;
      if (isMT) { assignedRole = 'owner'; }
      else if (!existingGenesis) { assignedRole = 'owner'; localStorage.setItem(`${BRAND_KEY}_genesis_owner`, emailLower); }
      await supabase.from('profiles').upsert({ id: authData.user.id, email: emailLower, full_name: fullName, role: assignedRole, app_name: APP_NAME });
      localStorage.setItem(`${BRAND_KEY}_password_changed`, 'true');
    }
  }

  async function signInWithToken(token) {
    try {
      const { data, error } = await supabase.from('demo_tokens').select('*').eq('token', token).eq('used', false).single();
      if (error || !data || new Date(data.expires_at) < new Date()) return { success: false, error: 'Token expired or invalid' };
      await supabase.from('demo_tokens').update({ used: true }).eq('id', data.id);
      localStorage.setItem(`${BRAND_KEY}_password_changed`, 'true');
      localStorage.setItem(`${BRAND_KEY}_nda_signed`, 'true');
      localStorage.setItem(`${BRAND_KEY}_tutorial_done`, 'true');
      setUser({ email: data.user_email, full_name: data.user_email.split('@')[0], role: data.user_role || 'owner', isOwner: (data.user_role || 'owner') === 'owner', isDemoSession: true });
      setOnboardingStep(null); setLoading(false);
      return { success: true };
    } catch (err) { return { success: false, error: err.message }; }
  }

  const completeOnboardingStep = (step) => {
    if (step === 'password') { localStorage.setItem(`${BRAND_KEY}_password_changed`, 'true'); setOnboardingStep('nda'); }
    else if (step === 'nda') { localStorage.setItem(`${BRAND_KEY}_nda_signed`, 'true'); setOnboardingStep('tutorial'); }
    else if (step === 'tutorial') { localStorage.setItem(`${BRAND_KEY}_tutorial_done`, 'true'); setOnboardingStep(null); }
  };

  const demoBypass = (email, role) => {
    localStorage.setItem(`${BRAND_KEY}_password_changed`, 'true');
    localStorage.setItem(`${BRAND_KEY}_nda_signed`, 'true');
    localStorage.setItem(`${BRAND_KEY}_tutorial_done`, 'true');
    setUser({ email: email || 'rmiskulin@gmail.com', full_name: email ? email.split('@')[0] : 'Ryan Miskulin', role: role || 'owner', isOwner: true, isDemoSession: true });
    setOnboardingStep(null);
  };

  const dismissPasskeyPrompt = () => { localStorage.setItem(`${BRAND_KEY}_passkey_dismissed`, 'true'); setShowPasskeyPrompt(false); };

  async function signOut() { await supabase.auth.signOut(); setUser(null); setOnboardingStep(null); setShowPasskeyPrompt(false); }

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signUp, signOut, signInWithToken, demoBypass, onboardingStep, completeOnboardingStep, showPasskeyPrompt, dismissPasskeyPrompt }}>
      {children}
    </AuthContext.Provider>
  );
}
